// src/utils/chords.ts
import { Scale, Chord, ChordType } from 'tonal';
import { ScaleDictionary } from 'tonal';
// Add chords missing from tonal
ChordType.add(["1P", "2M", "5d", "6M"], ['6sus2b5'], "six suspended second flat fifth");
ChordType.add(["1P", "2M", "5P", "7m"], ['7sus2'], "dominant seven suspended second");
ChordType.add(["1P"], ["1"], "single note")


// Define the Double Hungarian Minor scale
ScaleDictionary.add(["1P", "2m", "3M", "4P", "5P", "6m", "7m"], "double hungarian minor", ["double hungarian minor"]);

// Define chord qualities for each mode, since tonal doesn't provide this information
export const modeChords: { [key: string]: string[][] } = {
    'chromatic': [],
    'major': [['', 'sus4', 'maj7', 'add6', 'add9'], ['m', 'sus4', 'm7', 'm9', 'm11'], ['m', 'sus4', 'm7'], ['', 'sus2', 'maj7', 'add6'], ['', 'sus4', 'sus2', '7', '7sus4'], ['m', 'sus4', 'sus2', 'm7', 'm9'], ['dim', 'm7b5']],
    'minor': [['m', 'sus4', 'sus2', 'm7', 'm9'], ['dim', 'm7b5'], ['', 'sus4', 'sus2', 'maj7', 'add6'], ['m', 'sus4', 'sus2', 'm7', 'm9',], ['m', 'sus4', 'm7', 'm6'], ['', 'sus2', 'maj7', 'add6', 'maj9'], ['', 'sus4', 'sus2', '7', '9']],
    'dorian': [['m', 'm7', 'm9', 'm11'], ['m', 'm7', 'm6', 'm9'], ['', 'maj7', 'add6', 'maj9'], ['', '7', '9', '7sus4'], ['m', 'm7', 'm9', 'm11'], ['dim', 'm7b5'], ['', 'maj7', 'add6', 'add9']],
    'phrygian': [['m', 'm7', 'm9', 'm11'], ['', 'maj7', 'add6', 'sus4'], ['', '7', '9', '13'], ['m', 'm7', 'm9', 'm11'], ['dim', 'm7b5'], ['', 'maj7', 'add6', 'add9'], ['m', 'm7', 'm6']],
    'lydian': [['', 'maj7', 'add6', 'maj9'], ['', '7', '9', '7sus4', '13'], ['m', 'm7', 'm9', 'm11'], ['dim', 'm7b5'], ['', 'maj7', 'add6', 'maj9'], ['m', 'm7', 'm6'], ['m', 'm7', 'm9']],
    'mixolydian': [['', '7', '9', '7sus4', '13'], ['m', 'm7', 'm9', 'm11'], ['dim', 'm7b5'], ['', 'maj7', 'add6', 'maj9'], ['m', 'm7', 'm9'], ['m', 'm7', 'm6'], ['', 'maj7', 'add6', 'add9']],
    'aeolian': [['m', 'm7', 'm9', 'm11'], ['dim', 'm7b5'], ['', 'maj7', 'add6', 'maj9'], ['m', 'm7', 'm9', 'm11'], ['m', 'm7', 'm6'], ['', 'maj7', 'add6', 'add9'], ['', '7', '9', '13']],
    'locrian': [['dim', 'm7b5', 'dim7'], ['', 'maj7', 'add6', 'sus4'], ['m', 'm7', 'm9'], ['m', 'm7', 'm6'], ['', 'maj7', 'add6', 'maj9'], ['', '7', '9'], ['m', 'm7', 'm9']],
    'harmonic minor': [['m', 'mMaj7', 'mMaj9', 'madd9'], ['dim', 'm7b5'], ['maj7#5'], ['m', 'm7'], ['', '7', 'sus4'], ['', 'maj7'], ['dim', 'dim7']],
    'hungarian minor': [['m', 'sus2', 'dim', 'mMaj7', ], ['Mb5', '7b5'], ['aug', 'maj7#5',], ['6sus2b5'], ['', 'sus4', 'aug', 'maj7', 'maj7#5'], ['', 'm', 'dim', '7', 'maj7', 'min7'], ['', 'm', 'aug', 'm6']],
    'double hungarian minor': [['', '5', 'sus4', '7'], ['dim', 'dim7', 'maj7'], ['dim'], ['5', 'sus2'], ['dim'], ['1'], ['m7b5', 'sus2']],
    'melodic minor': [['m', 'mMaj7', 'mMaj9', 'm add9'], ['m', 'm7', 'm9'], ['', 'maj7#5'], ['7', '9', '13'], ['7', '9', '13'], ['m7b5'], ['m7b5']],
    'major pentatonic': [['', 'sus2', 'add6', 'add9'], ['sus2', 'sus4'], ['?'], ['sus2', 'sus4'], ['m', 'm7']],
    'minor pentatonic': [['m', 'sus4', 'm7'], ['?'], ['sus4', 'sus2'], ['?'], ['sus4']],
};


/**
 * Function to get the chord qualities for a given scale degree.
 * @param {string} scaleType - The type of the scale (e.g., 'major', 'dorian').
 * @param {number} degree - The scale degree (1-based index).
 * @returns {string[] | null} - The array of chord qualities for the given scale degree, or null if not found.
 */
export function getChordQualities(scaleType: string, degree: number): string[] | null {
    return modeChords[scaleType] ? modeChords[scaleType][degree - 1] : null;
}

/**
 * Function to get Roman numeral representation based on the degree and quality.
 * @param {number} degree - The scale degree (1-based index).
 * @param {string} quality - The chord quality (e.g., 'maj', 'm', 'm7', 'dim7', etc.).
 * @returns {string} - The Roman numeral representation of the chord.
 */
function getRomanNumeral(degree: number, quality: string): string {
    const numerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];
    const numeral = numerals[degree - 1];

    // Minor chords
    if (['m', 'm7', 'm9', 'mMaj7', 'mMaj9', 'm6'].includes(quality)) {
        return numeral.toLowerCase();
    }
    // Half-diminished chords
    if (quality === 'm7b5') {
        return numeral.toLowerCase() + 'ø';
    }
    // Fully diminished chords
    if (['dim', 'dim7'].includes(quality)) {
        return numeral.toLowerCase() + '°';
    }
    // Augmented chords
    if (quality.includes('#5')) {
        return numeral + '♯5';
    }

    // Major chords and other qualities not covered by specific conditions
    return numeral;
}

/**
 * Function to get common Roman numeral chords in a scale.
 * @param {string} scaleName - The name of the scale (e.g., 'C major', 'A minor').
 * @returns {Array<{numeral: string, root: string, chord: string[], name: string}>} - An array of objects representing the chords in the scale.
 */
export function getScaleChords(scaleName: string) {
    const scale = Scale.get(scaleName);
    const scaleType = scale.type; // e.g., 'major', 'dorian', 'mixolydian', etc.

    const chords: { numeral: string, root: string, chord: string[], name: string }[] = [];

    scale.notes.forEach((note, index) => {
        const degree = index + 1;
        const qualities = getChordQualities(scaleType, degree);
        if (qualities) {
            qualities.forEach(quality => {
                const chord = Chord.getChord(quality, note);
                chords.push({
                    numeral: getRomanNumeral(degree, quality),
                    root: note,
                    chord: chord.notes,
                    name: chord.symbol
                });
            });
        }
    });

    return chords;
}

/**
 * Function to get grouped Roman numeral chords in a scale by degree.
 * @param {string} scaleName - The name of the scale (e.g., 'C major', 'A minor').
 * @returns {Array<Array<{numeral: string, root: string, chord: string[], name: string}>>} - A nested array of objects representing the chords in the scale, grouped by degree.
 */
export function getGroupedScaleChords(scaleName: string) {
    const scale = Scale.get(scaleName);
    const scaleType = scale.type;

    const groupedChords: Array<Array<{ numeral: string, root: string, chord: string[], name: string }>> = [];

    scale.notes.forEach((note, index) => {
        const degree = index + 1;
        const qualities = getChordQualities(scaleType, degree);
        const chordsForDegree: { numeral: string, root: string, chord: string[], name: string }[] = [];

        if (qualities) {
            qualities.forEach(quality => {
                const chord = Chord.getChord(quality, note);
                chordsForDegree.push({
                    numeral: getRomanNumeral(degree, quality),
                    root: note,
                    chord: chord.notes,
                    name: chord.symbol
                });
            });
        }

        if (chordsForDegree.length > 0) {
            groupedChords.push(chordsForDegree);
        }
    });

    return groupedChords;
}
